import React from 'react';
import { Text, Pressable } from '../lib/react-native-compat';
import { barLabel } from './procurementFilters';

// Scope selector shared by the Uncommitted and Dates tabs.
//
// Two scopes only: the job picked in the top menu, or the team picked in the
// top menu. The request shape mirrors what the functions accept — { ids: [...] }
// or { teams: [...] } — so scope is resolved in SQL rather than by shipping
// thousands of job ids (see readUncommittedRollup.sql).

/** Request payload for the current scope. */
export const scopeRequest = (scope, forecastGuid) => {
    if (scope?.mode === 'job') return { forecastGuid, ids: [scope.job].filter(Boolean) };
    return { forecastGuid, teams: scope?.teams || [] };
};

/**
 * The most recent version guid — the live one, by Version_Start_DateTime, the
 * same way topMenu.js finds its liveVersionId. The procurement views use this
 * as the fallback when no version is picked in the top-menu selector.
 */
export const latestVersionGuid = (versions = []) => {
    let latest = null;
    let latestDate = null;
    (versions || []).forEach((v) => {
        const d = new Date(v?.Version_Start_DateTime);
        if (!Number.isNaN(d.getTime()) && (latestDate === null || d > latestDate)) {
            latestDate = d;
            latest = v.Version_GUID;
        }
    });
    return latest;
};

/** Short label for the current scope, for headers and export filenames. */
export const scopeLabel = (scope) => {
    if (scope?.mode === 'job') return scope.job ? `Job ${scope.job}` : 'No job selected';
    const teams = scope?.teams || [];
    if (teams.length === 0) return 'No team selected';
    if (teams.length === 1) return teams[0];
    return `${teams.length} teams`;
};

const chip = (active) => ({
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: active ? '#0f172a' : '#cbd5e1',
    backgroundColor: active ? '#0f172a' : '#ffffff',
    marginRight: 6,
    marginBottom: 6,
});

const chipText = (active) => ({
    fontSize: 12,
    color: active ? '#ffffff' : '#334155',
    fontWeight: active ? '700' : '500',
});

/**
 * Compact inline scope control — renders as a fragment so the parent toolbar
 * lays it out on the same row as its other filters. Both chips read from the
 * top menu: change the job or team up there and the scope follows.
 *
 * props:
 *   scope    – { mode: 'job' | 'teams', job?: string, teams?: string[] }
 *   onChange – (nextScope) => void
 *   job      – job number from the top-menu job selector, or null
 *   team     – team code from the top-menu team selector, or null
 */
const ProcurementScope = ({ scope, onChange, job = null, team = null }) => {
    const isJob = scope?.mode === 'job';
    const isTeam = !isJob;

    return (
        <>
            <Text style={barLabel}>SCOPE</Text>
            {job ? (
                <Pressable style={chip(isJob)} onPress={() => onChange({ mode: 'job', job })}>
                    <Text style={chipText(isJob)}>This job ({job})</Text>
                </Pressable>
            ) : null}
            <Pressable
                style={chip(isTeam)}
                onPress={() => onChange({ mode: 'teams', teams: team ? [team] : [] })}
            >
                <Text style={chipText(isTeam)}>{team ? `This team (${team})` : 'This team'}</Text>
            </Pressable>
        </>
    );
};

export default ProcurementScope;
